import React from 'react';
import ReportShell, { reportStyle, reportButtonStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { downloadCsv } from '../../../shared/csv';
import { formatProductScope } from './PriceTable';
import { formatNumber, formatPercent } from '../../../shared/numberFormat';

// TEMPORARY report, same status as MarginReportView.js (delete both once
// the full pricing engine lands) — a different question than that flat
// report, not a replacement for it: this pivots SKU/UoM against Base Qty
// (not each break's own raw UoM Qty), so a "10 Pack" break and a "1 Box of
// 100" break for the same or different SKUs land in the *same* column
// whenever they resolve to the same physical quantity (both 100 PCS) —
// that's the actual comparison this report exists for: does buying the
// same real quantity via a different UoM change the margin. Keying by raw
// UoM Qty instead would put them in unrelated columns even when the
// physical quantity is identical.
// Fixed-Override-only, Base-UoM-normalized-Cost/Price scope — deliberately
// narrower than MarginReportView.js, which now also plots Rule tiers
// benchmarked against Retail's Price. A Rule tier's effective price varies
// with Retail's own figure rather than being fixed, so it doesn't plot
// cleanly onto this matrix's shared Base-Qty axis (built from each row's
// own literal min_quantity) the way an unchanging Fixed Override price
// does; un-priced/Group-scoped rows stay excluded for the same reasons as
// the flat report.
function resolveProductCost(product) {
  if (!product) return null;
  if (product.supplier && product.cost_price_foreign !== null && product.cost_price_foreign !== undefined) {
    return Number(product.cost_price_foreign) * Number(product.supplier.exchange_rate);
  }
  if (product.cost_price === null || product.cost_price === undefined) return null;
  return Number(product.cost_price);
}

function flattenPricedRows(items) {
  const rows = [];
  items.forEach((item) => {
    if (item.rule || item.needs_price || !item.product || !item.uom_conversion_rate) return;
    const cost = resolveProductCost(item.product);
    if (cost === null) return;
    (item.breaks || []).forEach((brk) => {
      const exPrice = Number(brk.price);
      if (exPrice === 0) return;
      rows.push({ item, minQuantity: Number(brk.min_quantity), exPrice, cost });
    });
  });
  return rows;
}

function marginFor(row) {
  const pricePerBase = row.exPrice / row.item.uom_conversion_rate;
  return ((pricePerBase - row.cost) / pricePerBase) * 100;
}

function formatMargin(margin) {
  return formatPercent(margin);
}

function formatCustomerScopeFull(item) {
  if (item.customer) return item.customer.name;
  if (item.customer_price_group) return item.customer_price_group.name;
  return 'All Customers';
}

// SKU + UoM identifies a matrix row — a product with breaks in more than
// one UoM (e.g. Each and Pack) gets one row per UoM, not squashed
// together, since a Pack row and a Box row for the same SKU can still
// legitimately land in the same Base Qty column and need comparing
// against each other, not merging into one.
function rowKey(item) {
  return `${formatProductScope(item)}__${item.uom ? item.uom.code : ''}`;
}

function rowLabel(item) {
  const sku = formatProductScope(item);
  return item.uom ? `${sku} (${item.uom.code})` : sku;
}

// Base Qty (this break's own min_quantity, already stored in the
// Product's Base UoM) is the column key — keyed together with the Base
// UoM's own code, not the number alone, since two different Products in
// the same section could have different Base UoMs (e.g. PCS vs KG) where
// "100" means something different in each; that pairing is what actually
// identifies one column, the plain number just happens to be its visible
// label.
function baseQtyColumn(row) {
  const baseQty = Number(row.minQuantity);
  const baseUomCode = row.item.product?.base_uom?.code || '';
  return { key: `${baseQty}__${baseUomCode}`, baseQty, baseUomCode };
}

// Groups by Customer, then pivots within each group: one row per SKU/UoM,
// one column per distinct Base Qty that actually appears in that group —
// deliberately not a fixed/shared column set across groups, since
// different Customer sections can have entirely different products with
// entirely different break points.
function buildMatrixSections(flatRows) {
  const sectionsByCustomer = [];
  flatRows.forEach((row) => {
    const customerLabel = formatCustomerScopeFull(row.item);
    let section = sectionsByCustomer.find((s) => s.customerLabel === customerLabel);
    if (!section) {
      section = { customerLabel, rowsByKey: new Map(), columnsByKey: new Map() };
      sectionsByCustomer.push(section);
    }
    const key = rowKey(row.item);
    if (!section.rowsByKey.has(key)) {
      section.rowsByKey.set(key, { label: rowLabel(row.item), marginByColumn: new Map() });
    }
    const column = baseQtyColumn(row);
    section.columnsByKey.set(column.key, column);
    section.rowsByKey.get(key).marginByColumn.set(column.key, marginFor(row));
  });

  return sectionsByCustomer.map((section) => ({
    customerLabel: section.customerLabel,
    columns: [...section.columnsByKey.values()].sort((a, b) => a.baseQty - b.baseQty || a.baseUomCode.localeCompare(b.baseUomCode)),
    rows: [...section.rowsByKey.values()],
  }));
}

// Flat thresholds, not a distribution — simpler to read and to explain
// than a per-section quartile, and margin has an absolute meaning (money
// left over) that doesn't depend on what else happens to be in this
// section.
const RED_BELOW = 25;
const ORANGE_BELOW = 30;

function shadeFor(margin) {
  if (margin === undefined) return null;
  if (margin < RED_BELOW) return { color: '#b71c1c', background: 'rgba(211, 47, 47, 0.14)' };
  if (margin < ORANGE_BELOW) return { color: '#a15c00', background: 'rgba(245, 124, 0, 0.14)' };
  return null;
}

function buildCsvRows(items) {
  const sections = buildMatrixSections(flattenPricedRows(items));
  const csvRows = [];
  sections.forEach((section) => {
    section.rows.forEach((row) => {
      const csvRow = { Customer: section.customerLabel, 'SKU (UoM)': row.label };
      section.columns.forEach((col) => {
        csvRow[`${col.baseQty} ${col.baseUomCode}`] = row.marginByColumn.has(col.key) ? formatMargin(row.marginByColumn.get(col.key)) : '';
      });
      csvRows.push(csvRow);
    });
  });
  return csvRows;
}

function MarginMatrixReportView({ items, loading, filterSummary, onClose }) {
  const sections = loading ? [] : buildMatrixSections(flattenPricedRows(items));
  const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0);
  const isEmpty = !loading && totalRows === 0;

  return (
    <ReportShell
      title="Margin Matrix"
      subtitle={(
        <>
          <div>{`${formatReportTimestamp()} · ${totalRows} SKU/UoM row${totalRows === 1 ? '' : 's'}`}</div>
          <div>{`Override breaks only, one row per SKU/UoM. Columns are Base Qty (10 Pack = Box of 100 = "100 PCS"). Below ${RED_BELOW}% red, below ${ORANGE_BELOW}% orange.`}</div>
          {filterSummary && <div>Filtered by: {filterSummary}</div>}
        </>
      )}
      onClose={onClose}
      printDisabled={loading || isEmpty}
      actions={(
        <button
          type="button"
          onClick={() => downloadCsv('margin-matrix-report.csv', buildCsvRows(items))}
          disabled={loading || isEmpty}
          style={reportButtonStyle(loading || isEmpty)}
        >
          Download CSV
        </button>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No priced Fixed Override breaks match the current filters.</div>
      ) : (
        sections.map((section, sIdx) => (
          <div
            key={`${section.customerLabel}-${sIdx}`}
            style={sIdx > 0 ? { breakBefore: 'page', pageBreakBefore: 'always', marginTop: 24 } : undefined}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 15, borderBottom: reportStyle.tableHeaderBorder, paddingBottom: 6 }}>{section.customerLabel}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize, marginBottom: 20 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                  <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
                  {section.columns.map((col) => (
                    <th key={col.key} style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatNumber(col.baseQty, { maxDecimals: 4 })} {col.baseUomCode}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.rows.map((row) => (
                  <tr key={row.label} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '6px 8px 6px 0' }}>{row.label}</td>
                    {section.columns.map((col) => {
                      const margin = row.marginByColumn.get(col.key);
                      const shade = shadeFor(margin);
                      return (
                        <td
                          key={col.key}
                          style={{
                            padding: '6px 8px 6px 0',
                            textAlign: 'right',
                            fontWeight: shade ? 700 : 400,
                            color: shade ? shade.color : 'inherit',
                            background: shade ? shade.background : 'transparent',
                          }}
                        >
                          {margin !== undefined ? formatMargin(margin) : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </ReportShell>
  );
}

export default MarginMatrixReportView;
